import { IconName } from '@ardoq/icons';
import type { SelectOption } from '@ardoq/select';
import {
  type BubbleChartViewSettings,
  type FieldInfoKey,
  type BubbleChartSelectedFieldInfo,
  isLabelFilterValid,
  DIMENSION_TO_DATA_KEY,
} from '@ardoq/bubble-chart';
import { dispatchAction } from '@ardoq/rxbeach';
import { updateViewSettings } from '@ardoq/view-settings';
import { ViewIds } from '@ardoq/api-types';
import { debounce } from 'lodash';
import { t } from '@ardoq/i18n';
import type {
  LabelFilterContentViewProps,
  LabelFilterContentPropMapperArgs,
  FieldLabels,
} from './types';

type ClampValueArgs = {
  value: number;
  type: 'min' | 'max';
  minValue: number;
  maxValue: number;
};

const clampValue = ({
  value,
  type,
  minValue,
  maxValue,
}: ClampValueArgs): number => {
  const finite = Number.isFinite(value)
    ? value
    : type === 'min'
      ? minValue
      : maxValue;
  return type === 'min'
    ? Math.min(finite, maxValue)
    : Math.max(finite, minValue);
};

type FieldContext = {
  selectedField: FieldInfoKey;
  fieldName: string | BubbleChartSelectedFieldInfo;
};

const getFieldContext = (
  selectedField: FieldInfoKey | undefined,
  viewSettings: BubbleChartViewSettings
): FieldContext | null => {
  if (!selectedField) {
    return null;
  }

  const fieldName = viewSettings[selectedField];
  if (!fieldName) {
    return null;
  }

  return { selectedField, fieldName };
};

const getAvailableOptionsForSelect = (
  viewSettings: BubbleChartViewSettings,
  fieldLabels: FieldLabels
): SelectOption<FieldInfoKey>[] => {
  const options: Array<{
    key: FieldInfoKey;
    label: string | null;
    leftIcon: { name: IconName };
  }> = [
    {
      key: 'selectedFieldNameX',
      label: fieldLabels.x,
      leftIcon: { name: IconName.ARROW_RIGHT_ALT },
    },
    {
      key: 'selectedFieldNameY',
      label: fieldLabels.y,
      leftIcon: { name: IconName.ARROW_TOP_ALT },
    },
    {
      key: 'selectedFieldNameRadius',
      label: fieldLabels.radius,
      leftIcon: { name: IconName.BUBBLE_CHART },
    },
  ];

  return options
    .filter(option => viewSettings[option.key] && option.label !== null)
    .map(({ key, label, leftIcon }) => ({
      value: key,
      label: label!,
      leftIcon,
    }));
};

const debouncedDispatchLabelFilterUpdate = debounce(
  (
    dimension: FieldInfoKey,
    fieldName: string | BubbleChartSelectedFieldInfo,
    min: number,
    max: number
  ): void =>
    dispatchAction(
      updateViewSettings({
        viewId: ViewIds.BUBBLE,
        settings: {
          labelFilter: {
            dimension,
            fieldName,
            min,
            max,
          },
        },
        persistent: true,
      })
    ),
  700
);

export const toLabelFilterContentViewProps = ({
  getDimensionRange,
  viewSettings,
  fieldLabels,
  i18nLocale,
}: LabelFilterContentPropMapperArgs): LabelFilterContentViewProps => {
  const labelFilter = viewSettings.labelFilter;
  const selectedField =
    labelFilter &&
    isLabelFilterValid(labelFilter, viewSettings) &&
    fieldLabels[DIMENSION_TO_DATA_KEY[labelFilter.dimension]] !== null
      ? labelFilter.dimension
      : undefined;
  const minValue = labelFilter?.min ?? 0;
  const maxValue = labelFilter?.max ?? 0;

  const availableOptions = getAvailableOptionsForSelect(
    viewSettings,
    fieldLabels
  );

  const handleFieldChange = (value: FieldInfoKey | null) => {
    // Cancel any pending debounced updates when changing the selected field to prevent applying outdated filters
    debouncedDispatchLabelFilterUpdate.cancel();

    if (!value) {
      dispatchAction(
        updateViewSettings({
          viewId: ViewIds.BUBBLE,
          settings: { labelFilter: undefined },
          persistent: true,
        })
      );
      return;
    }

    const fieldName = viewSettings[value];
    if (!fieldName) {
      return;
    }

    const { min, max } = getDimensionRange(value);

    dispatchAction(
      updateViewSettings({
        viewId: ViewIds.BUBBLE,
        settings: {
          labelFilter: { dimension: value, fieldName, min, max },
        },
        persistent: true,
      })
    );
  };

  /** Ensures that the input value is a valid number and within the min-max range for the selected dimension. */
  const validateNumberInputValue = (
    value: number,
    type: 'min' | 'max'
  ): number => {
    if (!getFieldContext(selectedField, viewSettings)) {
      return value;
    }
    return clampValue({ value, type, minValue, maxValue });
  };

  const handleValueChange = (value: number, type: 'min' | 'max') => {
    const fieldContext = getFieldContext(selectedField, viewSettings);
    if (!fieldContext) {
      return;
    }

    if (!Number.isFinite(value)) {
      return;
    }

    if (type === 'min' && value > maxValue) {
      return;
    }

    if (type === 'max' && value < minValue) {
      return;
    }

    const clamped = clampValue({ value, type, minValue, maxValue });

    const nextMin = type === 'min' ? clamped : minValue;
    const nextMax = type === 'max' ? clamped : maxValue;

    debouncedDispatchLabelFilterUpdate(
      fieldContext.selectedField,
      fieldContext.fieldName,
      nextMin,
      nextMax
    );
  };

  return {
    description: t(
      i18nLocale,
      'tabview:bubbleChart.labelFilter.description',
      'Show fewer labels to keep your chart readable. Hidden labels still appear on hover.'
    ),
    fieldSelect: {
      label: t(
        i18nLocale,
        'tabview:bubbleChart.labelFilter.fieldLabel',
        'Field'
      ),
      placeholder: t(
        i18nLocale,
        'tabview:bubbleChart.labelFilter.fieldPlaceholder',
        'Select field'
      ),
      value: selectedField,
      options: availableOptions,
    },
    rangeInputs: selectedField
      ? {
          label: t(
            i18nLocale,
            'tabview:bubbleChart.labelFilter.rangeLabel',
            'Filter bubble names'
          ),
          min: {
            value: minValue,
            placeholder: t(
              i18nLocale,
              'tabview:bubbleChart.labelFilter.minPlaceholder',
              'min'
            ),
          },
          max: {
            value: maxValue,
            placeholder: t(
              i18nLocale,
              'tabview:bubbleChart.labelFilter.maxPlaceholder',
              'max'
            ),
          },
        }
      : undefined,
    actions: {
      onFieldChange: handleFieldChange,
      onValueChange: handleValueChange,
      onValueInputBlur: validateNumberInputValue,
    },
  };
};
